import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import Button from 'kingpin/atoms/Button';
import aguid from 'lib/aguid';

import ConfigureReportGadget from './ConfigureReportGadget';
import withoutNulls from '../../api/expression/withoutNulls';
import updateReportGadget from '../../api/updateReportGadget';

import { GadgetFormContainer } from '../GadgetForm';
import Row from '../Common/Row';
import CloseModal from '../CloseModal';
import Loading from '../Loading';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import isRemindersGadget from '../../types/visTypeCheckers/isRemindersGadget';
import AccountPickerContext from '../../contexts/AccountPickerContext';
import DateInputContext from '../../contexts/DateInputContext';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import useDashboardGadgetDataTypes from '../../hooks/useDashboardGadgetDataTypes';
import DateInputProvider from '../../contextProviders/DateInputProvider';
import { SortToggleProvider } from '../V5GadgetForm/SortToggleContext';
import MetricCacheContext from '../../contexts/MetricCacheContext';

const ConfigureReportGadgetContainer = ({
  editingGadget,
  close,
  onSaved,
  onEditSave,
  parentDateRange,
  parentRelativeDateRange,
  parentAdvancedRelativeDateRange,
  parentDateField,
}: {
  editingGadget?: ReportGadgets.ReportGadget;
  close: () => void;
  onSaved?: (gadget: ReportGadgets.ReportGadget) => void;
  // When given, the gadget is handed back here instead of being saved, for a
  // caller that saves it later (Studio saves on its own Save)
  onEditSave?: (gadget: ReportGadgets.ReportGadget) => void;
  parentDateRange?: DateRangeInput;
  parentRelativeDateRange?: RelativeDateRange;
  parentAdvancedRelativeDateRange?: AdvancedRelativeDateRange;
  parentDateField?: string;
}) => {
  const [chartDef, setChartDef] = useState<VisualisationDefinition | undefined>(
    () => (editingGadget ? editingGadget.chartDef : undefined),
  );

  // Date scope always comes from parent report
  // Only allow overriding the dateField
  const initialDateField =
    editingGadget?.dateField !== undefined
      ? editingGadget.dateField
      : parentDateField || 'date';

  return (
    <DateInputProvider
      initialDateRange={parentDateRange}
      initialRelativeDateRange={parentRelativeDateRange}
      initialAdvancedRelativeDateRange={parentAdvancedRelativeDateRange}
      initialDateField={initialDateField}
    >
      <ConfigureReportGadgetContent
        editingGadget={editingGadget}
        close={close}
        onSaved={onSaved}
        onEditSave={onEditSave}
        chartDef={chartDef}
        setChartDef={setChartDef}
        parentDateField={parentDateField}
      />
    </DateInputProvider>
  );
};

const ConfigureReportGadgetContent = ({
  editingGadget,
  close,
  onSaved,
  onEditSave,
  chartDef,
  setChartDef,
  parentDateField,
}: {
  editingGadget?: ReportGadgets.ReportGadget;
  close: () => void;
  onSaved?: (gadget: ReportGadgets.ReportGadget) => void;
  onEditSave?: (gadget: ReportGadgets.ReportGadget) => void;
  chartDef?: VisualisationDefinition;
  setChartDef: React.Dispatch<
    React.SetStateAction<VisualisationDefinition | undefined>
  >;
  parentDateField?: string;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { dateField, setDataTypes } = useContext(DateInputContext);
  const { requestAllNormalMetrics, requestAllCompoundMetrics } =
    useContext(MetricCacheContext);

  useEffect(() => {
    requestAllNormalMetrics();
    requestAllCompoundMetrics();
  }, [requestAllNormalMetrics, requestAllCompoundMetrics]);

  const usedDataTypes = useDashboardGadgetDataTypes(undefined, chartDef);

  useEffect(() => {
    setDataTypes(usedDataTypes);
  }, [usedDataTypes, setDataTypes]);
  const [scope, setScope] = useState<FilterPlateType[]>(
    editingGadget ? editingGadget.scope || [] : [],
  );
  const [comparison] = useState<PersistedComparisonType | undefined>(
    editingGadget ? editingGadget.comparison : undefined,
  );
  const [isDefaultFiltersDisabled, setIsDefaultFiltersDisabled] =
    useState<boolean>(
      editingGadget ? !!editingGadget.isDefaultFiltersDisabled : false,
    );
  const [defaultDatasetFiltersMode, setDefaultDatasetFiltersMode] = useState<
    'report' | 'enabled' | 'disabled'
  >(editingGadget?.defaultDatasetFiltersMode || 'report');

  const isValid = isRemindersGadget(chartDef)
    ? chartDef.events.length > 0 && chartDef.name !== ''
    : !!chartDef;

  const onSave = useCallback(() => {
    if (!chartDef) {
      return;
    }

    // Only save dateField if it differs from parent report's dateField
    const shouldSaveDateField =
      dateField !== undefined && dateField !== (parentDateField || 'date');

    const gadget: ReportGadgets.ReportGadget = withoutNulls({
      id: editingGadget ? editingGadget.id : aguid(),
      chartDef,
      scope,
      // Date scope is never saved - always driven by parent report
      dateRange: undefined,
      relativeDateRange: undefined,
      advancedRelativeDateRange: undefined,
      // Only save dateField if different from parent
      dateField: shouldSaveDateField ? dateField : undefined,
      comparison,
      isDefaultFiltersDisabled,
      defaultDatasetFiltersMode,
      // A Studio block's own settings. They stay with the gadget: they only
      // count while they still rebuild its chart (see readStudioBlock), so
      // one this changes simply reads as a classic gadget.
      ...(editingGadget && editingGadget.studio
        ? { studio: editingGadget.studio }
        : {}),
      createdBy: editingGadget ? editingGadget.createdBy : currentUser.id,
      createdOn: editingGadget
        ? editingGadget.createdOn
        : DateTime.utc().toISO(),
      updatedBy: currentUser.id,
      updatedOn: DateTime.utc().toISO(),
    });

    if (onEditSave) {
      onEditSave(gadget);
      return;
    }
    updateReportGadget(gadget.id, gadget, selectedAccountId).then(() => {
      if (onSaved) {
        onSaved(gadget);
      }
    });
  }, [
    chartDef,
    scope,
    dateField,
    comparison,
    isDefaultFiltersDisabled,
    defaultDatasetFiltersMode,
    currentUser.id,
    editingGadget,
    onEditSave,
    onSaved,
    selectedAccountId,
    parentDateField,
  ]);

  if (editingGadget && !chartDef) {
    return <Loading />;
  }

  return (
    <SortToggleProvider>
      <CloseModal close={close} />
      <Row
        style={{
          height: '80vh',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '25%',
            overflowY: 'scroll',
            borderRight: '1px solid var(--color-border-primary)',
          }}
        >
          <GadgetFormContainer
            selected={chartDef}
            onChange={setChartDef}
            isEditing={!!editingGadget}
            isReport={true}
          />
        </div>
        <div style={{ width: '75%' }}>
          <div
            style={{
              overflowY: 'scroll',
              height: 'calc(80vh - 56px)',
            }}
          >
            <FilterPlatesProvider
              scope={scope}
              setScope={setScope}
              dataTypes={usedDataTypes}
              isDefaultFiltersDisabled={isDefaultFiltersDisabled}
              setIsDefaultFiltersDisabled={setIsDefaultFiltersDisabled}
            >
              <ConfigureReportGadget
                chartDef={chartDef}
                isValid={!!isValid}
                isDefaultFiltersDisabled={isDefaultFiltersDisabled}
                setIsDefaultFiltersDisabled={setIsDefaultFiltersDisabled}
                defaultDatasetFiltersMode={defaultDatasetFiltersMode}
                setDefaultDatasetFiltersMode={setDefaultDatasetFiltersMode}
              />
            </FilterPlatesProvider>
          </div>
          <Row
            spaceBetween
            centerAlign
            style={{
              height: 56,
              paddingRight: 22,
              paddingLeft: 22,
              borderTop: '1px solid var(--color-border-primary)',
              alignItems: 'center',
            }}
          >
            <Button
              onClick={close}
              type="Secondary"
              size="Small"
              label="Close"
            />
            <div style={{ height: 30 }}>
              <Button
                onClick={onSave}
                isDisabled={!isValid}
                label="Save"
                type="Primary"
                size="Small"
              />
            </div>
          </Row>
        </div>
      </Row>
    </SortToggleProvider>
  );
};

export default ConfigureReportGadgetContainer;
